import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api-fetch";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ApiErrorBanner } from "@/components/ui/api-error-banner";
import { RefreshCw, List, Map } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { RecommendationResponse } from "@/types/recommendation";
import { RecommendationMap } from "./RecommendationMap";
import { DualRecommendationsList } from "./DualRecommendationsList";
import { reportUserError } from "@/lib/report-error";

interface RecommendationSystemProps {
  vagaId: string;
}

export function RecommendationSystem({ vagaId }: RecommendationSystemProps) {
  const [forceRefresh, setForceRefresh] = useState(false);
  const { toast } = useToast();

  const { data: recommendations, isLoading, error, refetch } = useQuery({
    queryKey: ["recommendations", vagaId, forceRefresh],
    queryFn: () => {
      // `dual=true` traz a lista paralela ("Outras opções") com pesos
      // aprendidos por regressão. Custo no backend é só re-ranqueamento
      // local — não dobra o tempo da request.
      const params = new URLSearchParams({ limit: "100", dual: "true" });
      if (forceRefresh) params.set("recalculate", "true");
      return apiFetch<RecommendationResponse>(
        `${SUPABASE_URL}recommendations/${vagaId}?${params.toString()}`
      );
    },
    enabled: !!vagaId,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const handleRefresh = async () => {
    setForceRefresh(true);
    try {
      await refetch();
      toast({
        title: "Recomendações atualizadas",
        description: "As recomendações foram recalculadas com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar as recomendações. " + reportUserError(error, { source: "recomendacoes" }).description,
        variant: "destructive",
      });
    } finally {
      setForceRefresh(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-6 w-40 bg-slate-200 animate-pulse rounded" />
          <div className="h-9 w-32 bg-slate-200 animate-pulse rounded" />
        </div>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-slate-100 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    // Mostra o detail real do servidor (não só "HTTP 500 …") — o backend
    // agora propaga `<TipoExcecao>: <mensagem>` no campo `detail`.
    return (
      <ApiErrorBanner
        error={error}
        title="Erro ao carregar recomendações"
        onRetry={handleRefresh}
      />
    );
  }

  if (!recommendations || recommendations.recommendations.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-6 text-center">
          <div className="text-slate-400 mb-4">
            <List className="h-12 w-12 mx-auto mb-2" />
            <h3 className="font-semibold text-lg">Nenhuma recomendação encontrada</h3>
            <p className="text-sm mt-1">
              Não há freelancers recomendados para esta vaga no momento.
            </p>
          </div>
          <Button onClick={handleRefresh} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Gerar recomendações
          </Button>
        </CardContent>
      </Card>
    );
  }

  const excellentCount = recommendations.recommendations.filter(r => r.scoring.recommendation_level === 'EXCELLENT').length;
  const goodCount = recommendations.recommendations.filter(r => r.scoring.recommendation_level === 'GOOD').length;
  const moderateCount = recommendations.recommendations.filter(r => r.scoring.recommendation_level === 'MODERATE').length;

  return (
    <div className="space-y-6">
      {/* Header com estatísticas */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Recomendações Inteligentes
            </h3>
            <p className="text-sm text-slate-600">
              {recommendations.total_recommendations} freelancers analisados
            </p>
          </div>
          
          <div className="flex gap-2">
            {excellentCount > 0 && (
              <Badge className="bg-green-100 text-green-800 border-green-300">
                {excellentCount} Excelente{excellentCount !== 1 ? 's' : ''}
              </Badge>
            )}
            {goodCount > 0 && (
              <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                {goodCount} Bo{goodCount === 1 ? 'm' : 'ns'}
              </Badge>
            )}
            {moderateCount > 0 && (
              <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                {moderateCount} Moderado{moderateCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-500">
            {recommendations.cache_status === 'hit' ? 'Cache' : 'Atualizado'} • {' '}
            {new Date(recommendations.generated_at).toLocaleString('pt-BR')}
          </div>
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            size="sm"
            disabled={forceRefresh}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${forceRefresh ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Tabs para visualizações */}
      <Card className="border-slate-200">
        <Tabs defaultValue="list" className="w-full">
          <div className="border-b border-slate-200 px-6 pt-6">
            <TabsList className="grid w-full max-w-md grid-cols-2 bg-slate-100/50 p-1 rounded-lg">
              <TabsTrigger 
                value="list" 
                className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:shadow-xs"
              >
                <List className="h-4 w-4" />
                Lista Rankeada
              </TabsTrigger>
              <TabsTrigger 
                value="map" 
                className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:shadow-xs"
              >
                <Map className="h-4 w-4" />
                Mapa de Quadrantes
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="list" className="p-6">
            <DualRecommendationsList
              primary={recommendations.recommendations}
              exploratory={recommendations.exploratory}
              vagaId={vagaId}
            />
          </TabsContent>

          <TabsContent value="map" className="p-6">
            <RecommendationMap 
              recommendations={recommendations.recommendations}
              vagaId={vagaId}
            />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
